// SignoffCard — renders a sign-off document inline (message_type Signoff).
// It renders fully from the message metadata first (so the card always looks
// complete, even offline), then fetches live detail from TMCHub to refresh
// approver state + enable signing for the current user.

import { useCallback, useEffect, useState } from "react";
import { SignoffsApi } from "../hooks/useApi.js";
import Icon from "./Icon.jsx";

const VARIANTS = ["a", "b", "c", "d", "e"];
const variantFor = (id) => VARIANTS[Math.abs(Number(id) || 0) % VARIANTS.length];
const initials = (n) => (n || "?").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

// Build the initial doc from the message metadata the webhook embedded.
function docFromMeta(message, currentUserId) {
  let meta = {};
  try { meta = JSON.parse(message.metadataJson || "{}"); } catch { /* ignore */ }
  if (!meta.docId) return null;
  const approvers = (meta.approvers || []).map((a) => ({
    userId: a.userId, name: a.name, signed: !!a.signed, signedAt: a.signedAt || null,
  }));
  const status = meta.status || "PendingSignoff";
  return {
    id: meta.docId,
    productName: meta.title || "Sign-off",
    version: meta.version || "",
    status,
    pdfUrl: meta.pdfUrl || null,
    approvers,
    demo: !!meta.demo, // tutorial seed — don't hit TMCHub for a fake doc
    canSign: status === "PendingSignoff" && approvers.some((a) => a.userId === currentUserId && !a.signed),
  };
}

export default function SignoffCard({ message, currentUserId, onOpenPdf }) {
  const [doc, setDoc] = useState(() => docFromMeta(message, currentUserId));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [removed, setRemoved] = useState(false);

  const refresh = useCallback(async () => {
    if (!doc?.id || doc.demo) return;
    try { const live = await SignoffsApi.get(doc.id); if (live) setDoc(live); }
    catch (e) { if (e?.status === 404) setRemoved(true); /* else keep the cached card */ }
  }, [doc?.id]);

  useEffect(() => { refresh(); }, [refresh]);

  async function sign() {
    setBusy(true); setError("");
    try { setDoc(await SignoffsApi.sign(doc.id, message.channelId)); }
    catch (e) { setError(e.body?.error || "Could not sign right now."); }
    finally { setBusy(false); }
  }

  if (!doc) return <div className="msg-system">📄 Sign-off document</div>;

  // The document was deleted in TMCHub since this card was posted — show the correct
  // state instead of stale "Pending" approver rows.
  if (removed) return (
    <div className="signoff signoff--removed">
      <div className="signoff__head">
        <span className="signoff__icon">🗑️</span>
        <div className="signoff__doc">
          <h4>{doc.productName}</h4>
          <span className="ver">Sign-off</span>
        </div>
        <span className="status-chip status-chip--open">Removed</span>
      </div>
      <p className="signoff__removed">This sign-off document was removed in TMCHub and is no longer available.</p>
    </div>
  );

  const signed = doc.approvers.filter((a) => a.signed).length;
  const total = doc.approvers.length || 1;
  const complete = doc.status === "FullySigned";
  const pct = Math.round((signed / total) * 100);
  const chip = complete ? "done" : signed > 0 ? "partial" : "pending";
  const chipLabel = complete ? "✓ Fully signed" : signed > 0 ? `${signed} of ${total} signed` : "Pending";

  return (
    <div className="signoff" data-tour="signoffcard" role="button" tabIndex={0} onClick={() => onOpenPdf?.(doc)}>
      <span className="card-scan" aria-hidden />
      <span className="card-corner card-corner--tr" aria-hidden />
      <span className="card-corner card-corner--bl" aria-hidden />
      <div className="signoff__head">
        <span className="signoff__icon"><Icon name={complete ? "check-square" : "document"} /></span>
        <div className="signoff__doc">
          <span className="signoff__eyebrow">Sign-off document</span>
          <h4>{doc.productName}</h4>
          <span className="ver">{[doc.version, doc.documentType].filter(Boolean).join(" · ") || "Sign-off"}</span>
        </div>
        <span className={`status-chip status-chip--${chip}`}>{chipLabel}</span>
      </div>

      <div className="signoff__bar"><i style={{ width: `${pct}%` }} /></div>

      {doc.approvers.length > 0 && (
        <ul className="signoff__approvers">
          {doc.approvers.map((a) => (
            <li key={a.userId}>
              <span className={`avatar avatar--sm avatar--${variantFor(a.userId)}`} aria-hidden>{initials(a.name)}</span>
              {a.name}
              <span className={`stat ${a.signed ? "ok" : "wait"}`}>{a.signed ? "✓ Signed" : "Awaiting"}</span>
            </li>
          ))}
        </ul>
      )}

      {error && <div className="field__err">⚠ {error}</div>}

      {doc.canSign && !complete && (
        <div className="signoff__foot">
          <button className="btn btn--primary btn--sm" disabled={busy} onClick={(e) => { e.stopPropagation(); sign(); }}>
            {busy ? <span className="btn__spin" /> : "Review & sign"}
          </button>
        </div>
      )}
    </div>
  );
}
